import { useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { awsImage, setUserCity } from '@firsttable/functions';
import ScrollContainer from '@discover-components/availability/carousel/scrollContainer';
import useDragToScroll from '@discover-lib/useDragToScroll';
import { cn } from '@discover-lib/utils';
import Skeleton from '@discover-ui/skeleton';
import { cuisineHref } from '@/lib/cityCuisine';
import { TrackProvider } from '@/components/analytics/context/AnalyticsContext';
import { useAnalytics } from '@/components/analytics/hooks/useAnalytics';
import PD6630CarouselHeader from './PD6630CarouselHeader';
import type { CityCuisine } from './getCityCuisines';
import type { PD6630Location } from './locations';
import usePD6630Cuisines from './usePD6630Cuisines';

const HEADING = 'Browse by cuisine';

const TILE_CLASS =
  'flex min-w-[120px] flex-1 shrink-0 basis-[120px] flex-col overflow-hidden rounded-lg border border-ds-gray-light-50 bg-white shadow-sm';

const IMAGE_CLASS = 'relative h-24 w-full shrink-0';

// Same right inset as the restaurant carousels, so the track ends on the See all button.
const TRACK_CLASS = 'mr-0 items-stretch gap-2 px-4 sm:mr-3 sm:pl-3 sm:pr-0 md:mr-0 md:px-0';

function CuisineTile({ city, cuisine }: { city: PD6630Location; cuisine: CityCuisine }) {
  const { track } = useAnalytics();
  const href = cuisineHref(city.slug, cuisine.slug);
  const imageSrc = cuisine.imagePath ? awsImage(cuisine.imagePath, 480, 400) : null;

  return (
    <Link
      href={href}
      draggable={false}
      onClick={() => {
        setUserCity(city.slug);
        track('link_clicked', {
          href,
          link_type: 'cuisine card',
          link_name: cuisine.title,
          link_copy: cuisine.title,
        });
      }}
      className={cn(
        TILE_CLASS,
        'no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ds-brand-700'
      )}>
      <span className={cn(IMAGE_CLASS, 'bg-ds-gray-light-100')}>
        {imageSrc ? (
          <Image
            src={imageSrc}
            alt=""
            fill
            sizes="160px"
            draggable={false}
            className="object-cover"
          />
        ) : null}
      </span>
      <span className="flex w-full min-w-0 items-center justify-center px-2 py-4">
        <span className="max-w-full truncate text-xs font-semibold text-ds-gray-light-950">
          {cuisine.title}
        </span>
      </span>
    </Link>
  );
}

function CuisineSkeleton() {
  return (
    <div className={TILE_CLASS} aria-hidden>
      <Skeleton className={cn(IMAGE_CLASS, 'rounded-none')} />
      <span className="flex w-full items-center justify-center px-2 py-4">
        <Skeleton className="h-5 w-16 rounded" />
      </span>
    </div>
  );
}

export default function PD6630CuisineCarousel({ city }: { city: PD6630Location }) {
  const router = useRouter();
  const scrollRef = useRef<HTMLDivElement>(null);
  const { cuisines, isLoading, hasError } = usePD6630Cuisines(city.id);
  const { isDragging, isScrollable, handleMouseDown, handleClickCapture } = useDragToScroll(
    scrollRef,
    { contentVersion: cuisines.length }
  );

  const unavailable = !isLoading && (hasError || cuisines.length === 0);
  if (unavailable) return null;

  const openCity = () => {
    setUserCity(city.slug);
    router.push(city.slug);
  };

  return (
    <TrackProvider
      trackEvent={isLoading ? undefined : 'component_interaction'}
      section="browse by cuisine"
      eventParams={{ component_type: 'carousel', action: 'viewed' }}>
      <section className="flex flex-col gap-5" aria-label={HEADING}>
        <PD6630CarouselHeader
          heading={HEADING}
          className="mb-0 lg:mb-0"
          onSeeAllClick={openCity}
          seeAllDisabled={isLoading}
        />
        <ScrollContainer
          ref={scrollRef}
          className={cn(
            TRACK_CLASS,
            isLoading && 'overflow-x-hidden',
            !isLoading && isScrollable && isDragging && 'cursor-grabbing',
            !isLoading && isScrollable && !isDragging && 'cursor-grab'
          )}
          onMouseDownCapture={isLoading ? undefined : handleMouseDown}
          onClickCapture={isLoading ? undefined : handleClickCapture}>
          {isLoading
            ? Array.from({ length: 8 }, (_, index) => <CuisineSkeleton key={index} />)
            : cuisines.map((cuisine) => (
                <CuisineTile key={cuisine.id} city={city} cuisine={cuisine} />
              ))}
        </ScrollContainer>
      </section>
    </TrackProvider>
  );
}
